import { PageViewConstructor } from '@trendhim/data-plans/lib/trendhim_ecom'
import type { IncomingMessage, ServerResponse } from 'http'
import filter from 'lodash/filter'
import includes from 'lodash/includes'
import isNil from 'lodash/isNil'
import join from 'lodash/join'
import map from 'lodash/map'
import merge from 'lodash/merge'
import omitBy from 'lodash/omitBy'
import some from 'lodash/some'
import uniq from 'lodash/uniq'
import { GetServerSidePropsContext, NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React from 'react'
import { SWRConfig } from 'swr'

import { Breadcrumb } from '@Components/Breadcrumbs/breadcrumb'
import { Event } from '@Events/Base'
import { PageView } from '@Events/PageView'
import { serverSideFetch as fetchAlgoliaData } from '@Globals/Algolia/Server'
import { BlockConfig, BlockConfigContext } from '@Globals/CMS/Context'
import { AnalyticsPageView } from '@Globals/Google/Analytics'
import { DataLayer } from '@Globals/Google/DataLayer'
import { AlternateLink } from '@Globals/Metadata'
import { PreviewData } from '@Globals/Preview'

export interface PageProps {
  alternateLinks?: AlternateLink[]
  blockConfig: Record<string, BlockConfig>
  openGraph?: {
    description?: string
    image?: string
    publishDate?: Date
    siteName?: string
    title?: string
    type?: string
  }
  page: {
    breadcrumbs?: Breadcrumb[]
    eventProps: PageViewConstructor
    id: string
    type: string
    url?: string
  }
  previewEnvironment: string | null
  seo?: {
    description?: string
    noIndex?: boolean
    title?: string
  }
  swrFallback: Record<string, any>
}

interface GenericHeaders {
  req: IncomingMessage
  res: ServerResponse
  surrogateKeys?: string[]
}

export const genericHeaders = ({ req, res, surrogateKeys }: GenericHeaders) => {
  // We don't need the domain part, but it's required to parse a url, so we use localhost as base
  const url = new URL(req.url || '', 'http://localhost:3000')

  res.setHeader('Surrogate-Key', join(uniq(['text', ...(surrogateKeys || [])]), ' '))

  // Skip caching for certain cookies
  if (some(['__next_preview_data', 'th_frontend_v2'], item => includes(req.headers.cookie, item))) {
    return
  }

  // Skip caching for certain query parameters
  if (some(['frontendv2', 'preview'], item => url.searchParams.has(item))) {
    return
  }

  res.setHeader('Surrogate-Control', 'max-age=604800')
  res.setHeader('Cache-Control', 'max-age=0, must-revalidate, post-check=0, pre-check=0, s-maxage=604800, stale-while-revalidate=86400, stale-if-error=86400')
}

export const pageProps = async (
  context: GetServerSidePropsContext<any, PreviewData>,
  pageType: string,
  pageID: string,
  pageEventProps: PageViewConstructor,
  data: any,
): Promise<PageProps> => {
  const {
    locale = '',
    previewData,
  } = context

  const props: PageProps = {
    alternateLinks: map(
      filter(data.alternateLinks || [], item => !!item.url?.length),
      item => ({ locale: item.locale || '', url: item.url || '' }),
    ),
    blockConfig: {},
    openGraph: omitBy({
      description: data.openGraph?.description,
      image: data.openGraph?.imageURL,
      siteName: data.openGraph?.siteName,
      title: data.openGraph?.title,
      type: data.openGraph?.type,
    }, isNil),
    page: {
      breadcrumbs: data?.breadcrumbs || [],
      eventProps: pageEventProps,
      id: pageID,
      type: pageType,
      url: data?.url,
    },
    previewEnvironment: previewData?.environment || null,
    seo: omitBy({
      description: data.seo?.description,
      noIndex: data.seo?.noIndex,
      title: data.seo?.title,
    }, isNil),
    swrFallback: {},
  }

  try {
    const algoliaData = await fetchAlgoliaData(context, data.blocks, locale)
    props.blockConfig = merge(props.blockConfig, algoliaData.blockConfig)
    props.swrFallback = { ...props.swrFallback, ...algoliaData.swrFallback }
  } catch (e) {
    console.error('failed to fetch algolia data', e)
  }

  return props
}

export const PageHOC = <T extends PageProps>(Component: NextPage<T>): NextPage<T> => (props) => {
  const {
    alternateLinks = [],
    blockConfig,
    openGraph,
    page,
    seo,
    swrFallback,
  } = props
  Event.setPage(page.type as any, page.id)

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { locales } = useRouter()

  const filteredAlternateLinks = filter(
    alternateLinks,
    (link) => {
      if (locales && locales.includes(link.locale) === false) {
        return false
      }

      return true
    },
  )
  return (
    <>
      <Head>
        {/* SEO */}
        {seo?.title && <title>{seo.title}</title>}
        {seo?.description && <meta key="description" content={seo.description} name="description" />}
        <meta key="robots" content={seo?.noIndex === true ? 'noindex, follow' : 'index, follow'} name="robots" />
        {page.url && <link key="canonical" href={page.url} rel="canonical" />}

        {/* Open Graph */}
        {openGraph?.description && <meta key="og:description" content={openGraph.description} property="og:description" />}
        {openGraph?.image && <meta key="og:image" content={openGraph.image} property="og:image" />}
        {openGraph?.siteName && <meta key="og:site_name" content={openGraph.siteName} property="og:site_name" />}
        {openGraph?.title && <meta key="og:title" content={openGraph.title} property="og:title" />}
        {openGraph?.type && <meta key="og:type" content={openGraph.type} property="og:type" />}
        {page.url && <meta key="og:url" content={page.url} property="og:url" />}

        {/* Alternate Links */}
        {map(filteredAlternateLinks, item => (
          <link
            key={item.locale}
            href={item.url}
            hrefLang={item.locale}
            rel="alternate"
          />
        ))}
      </Head>
      {/* Render component */}
      <SWRConfig value={{ fallback: swrFallback }}>
        <BlockConfigContext.Provider value={blockConfig}>
          <Component {...props} />
        </BlockConfigContext.Provider>
      </SWRConfig>

      {/* Tracking */}
      <DataLayer input={{ contentIds: page.id, pageType: page.type, type: page.type }} />
      <AnalyticsPageView />
      <PageView eventProps={page.eventProps} />
    </>
  )
}

